import React, { createContext, useContext, useEffect, useMemo, useState } from "react";

const CvStepNavigationContext = createContext(null);

export function CvStepNavigationProvider({ children, activeStep, setActiveStep, totalSteps = 0, onFinish }) {
  const [visitedSteps, setVisitedSteps] = useState(() => new Set([activeStep]));

  useEffect(() => {
    setVisitedSteps((prev) => {
      if (prev.has(activeStep)) return prev;
      return new Set([...prev, activeStep]);
    });
  }, [activeStep]);

  const value = useMemo(
    () => ({
      activeStep,
      setActiveStep,
      goToNextStep: () => {
        if (activeStep >= totalSteps - 1) {
          onFinish?.();
          return;
        }
        setActiveStep((prev) => Math.min(prev + 1, Math.max(0, totalSteps - 1)));
      },
      goToPrevStep: () => setActiveStep((prev) => Math.max(prev - 1, 0)),
      totalSteps,
      visitedSteps,
    }),
    [activeStep, setActiveStep, totalSteps, visitedSteps, onFinish]
  );

  return (
    <CvStepNavigationContext.Provider value={value}>
      {children}
    </CvStepNavigationContext.Provider>
  );
}

export function useCvStepNavigation() {
  const ctx = useContext(CvStepNavigationContext);
  if (!ctx) {
    return {
      activeStep: 0,
      setActiveStep: () => {},
      goToNextStep: () => {},
      goToPrevStep: () => {},
      totalSteps: 0,
      visitedSteps: new Set([0]),
    };
  }
  return ctx;
}

/**
 * Returns the index of the first section that is considered "unfilled".
 * Used to open CvDetails on the first incomplete section.
 */
export function getFirstUnfilledStepIndex(cvDetails) {
  if (!cvDetails) return 0;

  const steps = [
    { key: "summary", empty: (v) => !v || (typeof v === "string" && (!v.trim() || v === "No summary provided.")) },
    { key: "projects", empty: (v) => !Array.isArray(v) || v.length === 0 },
    { key: "experiences", empty: (v) => !Array.isArray(v) || v.length === 0 },
    { key: "portfolio", empty: (v) => v == null || v === "" },
    { key: "expertise", empty: (v) => !v || (Array.isArray(v) && v.length === 0) || (v && Array.isArray(v.expertise) && v.expertise.length === 0) },
    { key: "roles", empty: (v) => !Array.isArray(v) || v.length === 0 },
    { key: "aiEngineering", empty: (v) => !Array.isArray(v) || v.length === 0 },
    { key: "certifications", empty: (v) => !Array.isArray(v) || v.length === 0 },
    { key: "courses", empty: (v) => !Array.isArray(v) || v.length === 0 },
    { key: "languages", empty: (v) => !Array.isArray(v) || v.length === 0 },
    { key: "methods", empty: (v) => !Array.isArray(v) || v.length === 0 },
    { key: "educations", empty: (v) => !Array.isArray(v) || v.length === 0 },
  ];

  for (let i = 0; i < steps.length; i++) {
    const step = steps[i];
    const value = cvDetails[step.key];
    if (step.empty(value)) return i;
  }
  return 0;
}
